import { URL_ROOT } from '@seek/adv-constants';
import { useTranslations } from '@vocab/react';
import {
  AccordionItem,
  Divider,
  Stack,
  Text,
  TextLink,
} from 'braid-design-system';
import type React from 'react';
import { shallowEqual, useSelector } from 'react-redux';

import getTrackingAdapter from 'src/app/dataAnalytics';
import { FAQ_PRESSED } from 'src/app/dataAnalytics/schema/constants';
import {
  FEATURE_DATA_ANALYTICS,
  FEATURE_PER_UNIT_BILLING,
  useFeatureToggleData,
} from 'src/app/featureToggle';
import { sendLinkEvent } from 'src/app/utils/analytics';
import { mapPageIdToPageLinkName } from 'src/app/utils/analytics/utils/mapPageIdToPageLinkName';
import { useLocalisedRoutes } from 'src/app/views/hooks/useLocalisedRoutes';
import type AppState from 'src/types/appState';
import type { TealiumEventData } from 'src/types/trackingData';

import translations from './.vocab';

interface Props {
  onHelpTextExpand?: (linkData: TealiumEventData) => void;
}

export const Faqs = ({ onHelpTextExpand }: Props) => {
  const { t } = useTranslations(translations);

  const { pageId, tealium } = useSelector(
    (state: AppState) => ({
      pageId: state.session.pageId,
      tealium: state.tealium,
    }),
    shallowEqual,
  );

  const { localiseRoute } = useLocalisedRoutes();

  const isPageAnalyticsEnabled = useFeatureToggleData(FEATURE_DATA_ANALYTICS);
  const isPerUnitBilling = useFeatureToggleData(FEATURE_PER_UNIT_BILLING);

  const onToggle = (expanded: boolean, selectedQuestion: string) => {
    if (expanded) {
      const formattedSelectedQuestion = selectedQuestion.replace(/\s/g, '-'); // replace whitespaces with -

      const ANALYTICS_LINK_NAME = 'ecaja-faq-expanded';
      const EVENT_NAME_FAQ_EXPANDED = 'faq-expanded';

      if (isPageAnalyticsEnabled) {
        const eventData = {
          eventName: FAQ_PRESSED,
          objectInteraction: `${EVENT_NAME_FAQ_EXPANDED}[${formattedSelectedQuestion}]`,
        };
        getTrackingAdapter.sendLinkEvent(eventData);
      } else {
        const eventData: TealiumEventData = {
          linkName: `${ANALYTICS_LINK_NAME}[${formattedSelectedQuestion}]`,
          eventName: [EVENT_NAME_FAQ_EXPANDED, ANALYTICS_LINK_NAME],
          objectInteraction: `${EVENT_NAME_FAQ_EXPANDED}[${formattedSelectedQuestion}]`,
        };

        sendLinkEvent(tealium, eventData);
      }

      if (onHelpTextExpand && pageId) {
        const pageLinkName = mapPageIdToPageLinkName(pageId);
        onHelpTextExpand({
          eventName: [EVENT_NAME_FAQ_EXPANDED],
          linkName: `FAQ+on+${pageLinkName}|[${selectedQuestion}]`,
        });
      }
    }
  };

  const params = {
    Link: (children: React.ReactNode[]) => (
      <TextLink
        href={localiseRoute(URL_ROOT)}
        rel="noopener noreferrer"
        target="_blank"
      >
        {children}
      </TextLink>
    ),
  };

  const faqs = [
    {
      title: t('faqs-q1'),
      content: t('faqs-a1'),
    },
    {
      title: t('faqs-q2'),
      content: t('faqs-a2', params),
    },
    {
      title: t('faqs-q3'),
      content: t('faqs-a3'),
    },
    {
      title: t('faqs-q4'),
      content: t('faqs-a4', params),
    },
    {
      title: t('faqs-q5'),
      content: t('faqs-a5', params),
    },
  ];

  if (isPerUnitBilling) {
    faqs.push({
      title: t('faqs-q6'),
      content: [
        t('faqs-a6'),
        t('faqs-a6-2'),
        t('faqs-a6-3'),
        <ol key="faq-pub-list" style={{ marginBlockStart: 0 }}>
          <li>{t('faqs-a6-4')}</li>
          <li>{t('faqs-a6-5')}</li>
        </ol>,
      ],
    });
  }

  return (
    <Stack space="medium">
      <Divider />
      <Stack space="xlarge">
        {faqs.map(({ title, content }) => (
          <AccordionItem
            id={title}
            key={title}
            label={title}
            onToggle={(expanded) => onToggle(expanded, title)}
          >
            {Array.isArray(content) ? (
              <Stack space="large">
                {content.map((item, index) => (
                  <Text key={`faq-pub-${index}`} tone="secondary">
                    {item}
                  </Text>
                ))}
              </Stack>
            ) : (
              <Text tone="secondary">{content}</Text>
            )}
          </AccordionItem>
        ))}
      </Stack>
    </Stack>
  );
};
